import React, { useContext, useState } from "react";

import {
    Card,
    Content,
    HeaderSection,
    NetworkSection,
    ImageWrapper,
    PrinterImage,
    NoImage,
    Details,
    Title,
    Info,
    MetaSection,
    Tooltip,
    TooltipWrapper,
} from "./PrinterCard.styles";

import { PrintersRegistryInfo } from "@grabcad/company-server-shared/dist/types";
import PrinterDataSources from "./PrinterDataSources/PrinterDataSources";
import { ApplicationContext } from "../../../../../components/ApplicationProvider";
import { config } from "../../../../../config";
import IpAddress from "./PrinterIpAddress/IpAddress";

export type PrinterCardProps = {
    serial: PrintersRegistryInfo["serial"];
    printerType: PrintersRegistryInfo["printerType"];
    printerTechnology: PrintersRegistryInfo["printerTechnology"];
    dataSources: PrintersRegistryInfo["dataSources"];
    lastSeenOn: PrintersRegistryInfo["lastSeenOn"];
    clientDeviceThingName?: string;
    ipAddress?: string;
    imageUri?: string;
    onShowMore?: () => void;
};

const DEFAULT_IMAGE = `${config.REACT_APP_PRINTER_IMAGE_BASE_URL}/default.png`;

const PrinterCard: React.FC<PrinterCardProps> = ({
    serial,
    printerType,
    printerTechnology,
    imageUri,
    dataSources,
    lastSeenOn,
    clientDeviceThingName,
    ipAddress,
}) => {
    const [failed, setFailed] = useState(false);

    const { t } = useContext(ApplicationContext);

    return (
        <Card className="printer-card" data-testid={`printer-card-${serial}`}>
            <Content>
                <HeaderSection className="printer-header" data-testid="printer-header">
                    <ImageWrapper>
                        {imageUri ? (
                            <PrinterImage
                                src={failed ? DEFAULT_IMAGE : imageUri}
                                alt={printerType}
                                loading="lazy"
                                decoding="async"
                                onError={() => setFailed(true)}
                            />
                        ) : (
                            <NoImage data-testid="printer-no-image">
                                {t("devices.printer_card.no_image")}
                            </NoImage>
                        )}
                    </ImageWrapper>

                    <Details>
                        <TooltipWrapper>
                            <Title data-testid="printer-title">{printerType}</Title>
                            <Tooltip data-testid="printer-type-tooltip">
                                {t("devices.printer_card.printer_type")}
                            </Tooltip>
                        </TooltipWrapper>

                        <TooltipWrapper>
                            <Info>{printerTechnology}</Info>
                            <Tooltip data-testid="printer-technology-tooltip">
                                {t("devices.printer_card.printer_technology")}
                            </Tooltip>
                        </TooltipWrapper>

                        <TooltipWrapper>
                            <Info>{serial}</Info>
                            <Tooltip data-testid="printer-serial-tooltip">
                                {t("devices.printer_card.printer_serial")}
                            </Tooltip>
                        </TooltipWrapper>
                    </Details>
                </HeaderSection>

                <NetworkSection className="printer-network" data-testid="printer-network">
                    <IpAddress
                        clientDeviceThingName={clientDeviceThingName}
                        ipAddress={ipAddress}
                    />
                </NetworkSection>

                {Boolean(dataSources?.length) && (
                    <MetaSection data-testid="printer-meta">
                        <Info>{t("devices.printer_card.cloud_connection")}</Info>
                        <PrinterDataSources dataSources={dataSources} lastSeenOn={lastSeenOn} />
                    </MetaSection>
                )}
            </Content>
        </Card>
    );
};

export default PrinterCard;
